import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import WhatsAppButton from "./components/WhatsAppButton";
import Home from "./pages/Home";
import ByLaws from "./pages/ByLaws";
import Amenities from "./pages/Amenities";
import AmenitiesBooking from "./pages/AmenitiesBooking";
import AppointmentBooking from "./pages/AppointmentBooking";
import ECDashboard from "./pages/ECDashboard";
import AmenitiesAvailability from "./pages/AmenitiesAvailability";
import InvestmentLocation from "./pages/InvestmentLocation";
import AdminDashboard from "./pages/AdminDashboard";
import Services from "./pages/Services";
import ApartmentMarketplace from "./pages/ApartmentMarketplace";
import ApartmentDetail from "./pages/ApartmentDetail";
import PostApartment from "./pages/PostApartment";
import MyApartments from "./pages/MyApartments";
import AdminApartments from "./pages/AdminApartments";
import AdminApartmentModeration from "./pages/AdminApartmentModeration";
import Login from "./pages/Login";
import { lazy, Suspense } from "react";

// Lazy-loaded Community Orders pages (hidden feature, no need to bundle upfront)
const OrderLanding = lazy(() => import("./pages/orders/OrderLanding"));
const VendorDetail = lazy(() => import("./pages/orders/VendorDetail"));
const OrderConfirmation = lazy(() => import("./pages/orders/OrderConfirmation"));
const VendorSignup = lazy(() => import("./pages/orders/VendorSignup"));
const AdminOrdersDashboard = lazy(() => import("./pages/orders/AdminOrdersDashboard"));
const AdminVendorManagement = lazy(() => import("./pages/orders/AdminVendorManagement"));
const AdminVendorDetail = lazy(() => import("./pages/orders/AdminVendorDetail"));

// Lazy-loaded Maintenance Request pages (hidden feature, stealth mode)
const MaintenanceSubmit = lazy(() => import("./pages/maintenance/MaintenanceSubmit"));
const MaintenanceMyRequests = lazy(() => import("./pages/maintenance/MaintenanceMyRequests"));
const MaintenanceRequestDetail = lazy(() => import("./pages/maintenance/MaintenanceRequestDetail"));
const AdminMaintenanceDashboard = lazy(() => import("./pages/maintenance/AdminMaintenanceDashboard"));
const AdminMaintenanceDetail = lazy(() => import("./pages/maintenance/AdminMaintenanceDetail"));

function LazyFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
    </div>
  );
}

function Router() {
  // make sure to consider if you need authentication for certain routes
  return (
    <Switch>
      <Route path={"/"} component={Home} />
      <Route path={"/login"} component={Login} />
      <Route path={"/admin-login"} component={Login} />
      <Route path={"/bylaws"} component={ByLaws} />
      <Route path={"/amenities"} component={Amenities} />
      <Route path={"/amenities-booking"} component={AmenitiesBooking} />
      <Route path={"/amenities-availability"} component={AmenitiesAvailability} />
      <Route path={"/appointments"} component={AppointmentBooking} />
      <Route path={"/ec-dashboard"} component={ECDashboard} />
      <Route path={"/investment-location"} component={InvestmentLocation} />
      <Route path={"/admin"} component={AdminDashboard} />
      <Route path={"/services"} component={Services} />
      <Route path={"/apartments"} component={ApartmentMarketplace} />
      <Route path={"/apartments/:id"}>
        {(params) => <ApartmentDetail id={params.id} />}
      </Route>
      <Route path={"/post-apartment"} component={PostApartment} />
      <Route path={"/my-apartments"} component={MyApartments} />
      <Route path={"/admin/apartments"} component={AdminApartments} />
      <Route path={"/admin/apartments-moderation"} component={AdminApartmentModeration} />

      {/* Community Orders routes (noindex, feature-gated) */}
      <Route path={"/order"}>
        <Suspense fallback={<LazyFallback />}><OrderLanding /></Suspense>
      </Route>
      <Route path={"/order/vendor/:slug"}>
        {(params) => <Suspense fallback={<LazyFallback />}><VendorDetail slug={params.slug} /></Suspense>}
      </Route>
      <Route path={"/order/confirmation/:orderNumber"}>
        {(params) => <Suspense fallback={<LazyFallback />}><OrderConfirmation orderNumber={params.orderNumber} /></Suspense>}
      </Route>
      <Route path={"/order/vendor-signup"}>
        <Suspense fallback={<LazyFallback />}><VendorSignup /></Suspense>
      </Route>
      <Route path={"/admin/orders"}>
        <Suspense fallback={<LazyFallback />}><AdminOrdersDashboard /></Suspense>
      </Route>
      <Route path={"/admin/vendors"}>
        <Suspense fallback={<LazyFallback />}><AdminVendorManagement /></Suspense>
      </Route>
      <Route path={"/admin/vendors/:id"}>
        {(params) => <Suspense fallback={<LazyFallback />}><AdminVendorDetail id={params.id} /></Suspense>}
      </Route>

      {/* Maintenance Request routes (noindex, feature-gated, stealth mode) */}
      <Route path={"/maintenance/new"}>
        <Suspense fallback={<LazyFallback />}><MaintenanceSubmit /></Suspense>
      </Route>
      <Route path={"/maintenance"}>
        <Suspense fallback={<LazyFallback />}><MaintenanceMyRequests /></Suspense>
      </Route>
      <Route path={"/maintenance/:id"}>
        {(params) => <Suspense fallback={<LazyFallback />}><MaintenanceRequestDetail id={params.id} /></Suspense>}
      </Route>
      <Route path={"/admin/maintenance"}>
        <Suspense fallback={<LazyFallback />}><AdminMaintenanceDashboard /></Suspense>
      </Route>
      <Route path={"/admin/maintenance/:id"}>
        {(params) => <Suspense fallback={<LazyFallback />}><AdminMaintenanceDetail id={params.id} /></Suspense>}
      </Route>

      <Route path={"/404"} component={NotFound} />
      {/* Final fallback route */}
      <Route component={NotFound} />
    </Switch>
  );
}

// NOTE: About Theme
// - First choose a default theme according to your design style (dark or light bg), than change color palette in index.css
//   to keep consistent foreground/background color across components
// - If you want to make theme switchable, pass `switchable` ThemeProvider and use `useTheme` hook

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider
        defaultTheme="light"
        switchable
      >
        <TooltipProvider>
          <Toaster />
          <Router />
          <WhatsAppButton />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
